import { FileStatus } from './FileStatus';
import { FileTrackerError } from './FileTrackerError';
import {
  FileTrackerStageEnum,
  FileTrackerListFileProps,
  FileTrackerListProps,
  FileTrackerDetailProps,
  FileTrackerTabEnum,
  isFileLoadingProps,
} from '../file-tracker-types';
import { Tabs } from '@ssar-app/uikit';
import { startCase } from 'lodash';
import { Table } from 'antd';
import { useState, useCallback, useEffect } from 'react';
import dayjs from 'dayjs';
import { TablePaginationConfig } from 'antd/lib/table';
import { Flex, Text } from '@chakra-ui/react';

interface FileListProps {
  isQuickViewRequired: boolean;
  getFileStatus?: (
    val: FileTrackerListFileProps,
  ) => Promise<FileTrackerDetailProps | undefined>;
  getFileList?: () => Promise<FileTrackerListProps | Error>;
  isFileTrackerLoading?: isFileLoadingProps;
  setIsFileTrackerLoading?: (val1: isFileLoadingProps) => void;
  subCode: string;
}

export const FileList = (props: FileListProps) => {
  const {
    isQuickViewRequired,
    getFileStatus,
    getFileList,
    isFileTrackerLoading,
    setIsFileTrackerLoading,
    subCode,
  } = props;
  const [files, setFileList] = useState<FileTrackerListFileProps[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [tabValue, setTabValue] = useState('totalFiles');
  const [error, setError] = useState<Error | null>(null);
  const [fileCount, setFileCount] = useState({
    totalFiles: 0,
    received: 0,
    inProgress: 0,
    scanCompleted: 0,
    sampling: 0,
    available: 0,
    failed: 0,
  });
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    hideOnSinglePage: false,
    defaultCurrent: 1,
    current: 1,
    total: 0,
  });

  const columns = [
    {
      title: 'Client',
      dataIndex: 'client',
      key: 'clientIdentifier',
      width: 80,
    },
    {
      title: 'Product',
      dataIndex: 'product',
      key: 'productName',
      width: 80,
    },
    {
      title: 'File Name',
      dataIndex: 'fileName',
      width: 400,
      render: (text: string) => <Text wordBreak='break-word'>{text}</Text>,
    },
    {
      title: 'File Version',
      dataIndex: 'fileId',
      key: 'fileId',
      width: 250,
    },
    {
      title: 'Layer',
      dataIndex: 'layer',
      key: 'Layer',
      width: 150,
    },
    {
      title: 'Date & Time',
      dataIndex: 'stageUpdateTime',
      render: (text: string) => {
        return dayjs(text).format('YYYY-MMM-DD HH:mm:ss [GMT]ZZ');
      },
      width: 250,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 350,
      render: (_text: string, row: FileTrackerListFileProps) => {
        return (
          <FileStatus
            {...{
              record: row,
              getFileStatus: getFileStatus,
              isQuickViewRequired,
            }}
          />
        );
      },
    },
  ];

  const fileStatuses = [
    [FileTrackerStageEnum.FILE_RECEIVED, FileTrackerStageEnum.FILE_IS_RECEIVED],
    FileTrackerStageEnum.MALWARE_SCAN_INPROGRESS,
    FileTrackerStageEnum.MALWARE_SCAN_COMPLETED,
    [
      FileTrackerStageEnum.FILE_SAMPLING_IN_PROGRESS,
      FileTrackerStageEnum.FILE_SAMPLING_INPROGRESS,
    ],
    FileTrackerStageEnum.FILE_IS_AVAILABLE,
    [
      FileTrackerStageEnum.MALWARE_SCAN_FAILED,
      FileTrackerStageEnum.FILE_SAMPLING_FAILED,
    ],
  ];

  const getFileStage = (
    filetrackerData: FileTrackerListProps,
    statusKey: string[],
  ) => {
    return (filetrackerData.files || [])
      .filter((a: FileTrackerListFileProps) => statusKey.includes(a.stage))
      .map((a: FileTrackerListFileProps) => ({
        ...a,
        product: filetrackerData.product,
        client: filetrackerData.clientIdentifier,
      }));
  };
  const setStatusFilesList = (
    filetrackerData: FileTrackerListProps,
    paginationInput: TablePaginationConfig,
  ) => {
    const [
      fileReceived,
      fileInProgress,
      fileScanCompleted,
      fileSampling,
      fileAvailable,
      filesFailed,
    ] = fileStatuses.map((status) =>
      getFileStage(
        filetrackerData,
        typeof status === 'object' ? status : [status],
      ),
    );
    let FilesLength;
    setFileCount({
      totalFiles: filetrackerData.files ? filetrackerData.files.length : 0,
      received: fileReceived.length,
      inProgress: fileInProgress.length,
      scanCompleted: fileScanCompleted.length,
      sampling: fileSampling.length,
      available: fileAvailable.length,
      failed: filesFailed.length,
    });
    switch (tabValue) {
      case FileTrackerTabEnum.received:
        setFileList(fileReceived);
        FilesLength = fileReceived.length;
        break;
      case FileTrackerTabEnum.inProgress:
        setFileList(fileInProgress);
        FilesLength = fileInProgress.length;
        break;
      case FileTrackerTabEnum.scanCompleted:
        setFileList(fileScanCompleted);
        FilesLength = fileScanCompleted.length;
        break;
      case FileTrackerTabEnum.sampling:
        setFileList(fileSampling);
        FilesLength = fileSampling.length;
        break;
      case FileTrackerTabEnum.available:
        setFileList(fileAvailable);
        FilesLength = fileAvailable.length;
        break;
      case FileTrackerTabEnum.failed:
        setFileList(filesFailed);
        FilesLength = filesFailed.length;
        break;
      default:
        setFileList(
          (filetrackerData.files || []).map((a: FileTrackerListFileProps) => ({
            ...a,
            product: filetrackerData.product,
            client: filetrackerData.clientIdentifier,
          })),
        );
        FilesLength = filetrackerData.files.length;
    }
    const paging = {
      pageSize: paginationInput?.pageSize || 10,
      current: paginationInput?.current || 1,
      total: FilesLength,
    };
    const mergedPagination = {
      ...pagination,
      ...paging,
    };
    mergedPagination.current = +mergedPagination.current;
    mergedPagination.pageSize = +mergedPagination.pageSize;
    setPagination(mergedPagination);
  };

  const fetchData = async (paginationInput: TablePaginationConfig) => {
    if (getFileList) {
      setIsLoading(true);
      const filesList = await getFileList();
      if (filesList instanceof Error) {
        setError(filesList);
      } else {
        filesList && setStatusFilesList(filesList, paginationInput);
      }
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (subCode !== '') {
      fetchData(pagination);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subCode, tabValue]);

  useEffect(() => {
    setIsFileTrackerLoading &&
      isFileTrackerLoading &&
      setIsFileTrackerLoading({
        ...isFileTrackerLoading,
        ...{ fileList: isLoading },
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const tabsData = useCallback(() => {
    return (
      <Table
        showSorterTooltip={false}
        onChange={fetchData}
        size='small'
        bordered={true}
        rowKey={(row) => row.fileId}
        loading={isLoading}
        dataSource={files}
        columns={columns}
        pagination={{
          ...pagination,
          showTotal: (total, range) =>
            `Showing ${range[0]}-${range[1]} of ${total} items`,
          position: ['bottomLeft'],
          showSizeChanger: true,
          simple: false,
        }}
      ></Table>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData, files, isLoading, pagination]);
  return (
    <>
      {!error ? (
        files && (pagination?.total || 1) > 0 ? (
          <Flex>
            <Tabs
              items={Object.entries(fileCount).map((fCount) => ({
                key: fCount[0],
                label: `${startCase(fCount[0])} (${fCount[1]})`,
                content: tabsData(),
              }))}
              onChange={setTabValue}
              colorScheme='blue'
              variant='unstyled'
              tabProps={{
                p: 1,
                _selected: {
                  border: '0.6px solid blue.800',
                  bg: 'blue.800',
                  color: 'white',
                },
                _hover: { color: 'blue.900' },
                border: '1px solid var(--chakra-colors-gray-300)',
              }}
              tabListProps={{ gap: 0 }}
              tabPanelProps={{ pl: 0, pr: 0 }}
              applyBorderRadiusForStartEndButtons={true}
              borderRadiusForStartEndButtons={5}
            />
          </Flex>
        ) : (
          <div>No Permission.</div>
        )
      ) : (
        <FileTrackerError
          reloadFn={() => {
            setError(null);
            fetchData(pagination);
          }}
          errorMsg='We encountered an unexpected error while getting the list of files. Please try again.'
        />
      )}
    </>
  );
};
